/**
 * Utilities for normalizing PascalCase dictionary keys to camelCase.
 *
 * GetShowsDivisions and similar stored-procedure endpoints return
 * `Dictionary<string, object>[]` where System.Text.Json skips the naming policy
 * for dictionary keys, leaving them PascalCase while the rest of the app expects
 * camelCase.
 */

/**
 * Mirror of .NET's camelCase policy: lower-cases a leading run of capitals
 * rather than just the first letter.
 *
 * Examples:
 * - `ID` → `id` (not `iD`)
 * - `IDValue` → `idValue`
 * - `ClassUuid` → `classUuid`
 */
export function toCamelCase(key: string): string {
  const chars = key.split('');

  for (let i = 0; i < chars.length; i++) {
    // A capital followed by a lower-case letter starts the next word and has
    // to stay capitalised, e.g. `IDValue` → `idValue`.
    if (i > 0 && i + 1 < chars.length && /[a-z]/.test(chars[i + 1])) {
      break;
    }
    if (!/[A-Z]/.test(chars[i])) {
      break;
    }
    chars[i] = chars[i].toLowerCase();
  }

  return chars.join('');
}

/**
 * Same as {@link normalizeRows} but recurses into nested objects and arrays.
 *
 * Needed for payloads whose rows carry children — a completed order holds
 * entries, which hold entry details, which hold line items. A shallow pass
 * renames the order's own keys and leaves every child PascalCase.
 */
export function normalizeKeysDeep<T = any>(value: unknown): T {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeKeysDeep(item)) as unknown as T;
  }

  // Dates and nulls are values, not shapes to rewrite.
  if (value === null || typeof value !== 'object' || value instanceof Date) {
    return value as T;
  }

  const normalized: Record<string, unknown> = {};
  Object.keys(value).forEach((key) => {
    normalized[toCamelCase(key)] = normalizeKeysDeep((value as Record<string, unknown>)[key]);
  });
  return normalized as T;
}

/**
 * Normalise an array of PascalCase dictionary rows to camelCase.
 *
 * @param rows Array of objects with PascalCase keys
 * @returns Array of objects with camelCase keys
 */
export function normalizeRows<T = any>(rows: unknown): T[] {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows.map((row) => {
    const normalized: Record<string, unknown> = {};
    Object.keys(row ?? {}).forEach((key) => {
      normalized[toCamelCase(key)] = row[key];
    });
    return normalized as T;
  });
}
